import { type FC } from 'react';
import cx from 'classnames';

import { IconPlus } from 'src/assets/icons/Plus';
import {
  BLANK_PROJECT_LABEL,
  BLOG_WIZARD_LABEL,
  BLOG_WIZARD_TOOLTIP,
  CONTENT_IMPROVER_LABEL,
  CONTENT_IMPROVER_TOOLTIP,
  LOCALIZATION_LABEL,
  LOCALIZATION_TOOLTIP,
  TEMPLATES_LABEL,
  TEMPLATES_TOOLTIP,
} from 'src/data/ui';

import { IconBlogWizard } from '../../assets/IconBlogWizard';
import { IconGlobe } from '../../assets/IconGlobe';
import { IconImprove } from '../../assets/IconImprove';
import { IconTemplates } from '../../assets/IconTemplates';
import { ActionButton } from '../../components/ActionButton';
import { WIDGET_ACTION, type WidgetActionHandler } from '../../lib';

import styles from './Actions.module.scss';

interface ActionsProps {
  className: string;
  onAction: WidgetActionHandler;
  disabled?: boolean;
}

export const Actions: FC<ActionsProps> = ({ className, onAction, disabled }) => {
  return (
    <div className={cx(styles.root, className)}>
      <ActionButton
        title={BLANK_PROJECT_LABEL}
        Icon={IconPlus}
        tooltip="Start a project from scratch"
        onClick={() => onAction(WIDGET_ACTION.createBlankProject)}
        disabled={disabled}
        customColors={{
          backgroundDefault: 'var(--color-primary-default)',
          backgroundHover: 'var(--color-primary-active)',
          icon: 'var(--color-gray-white)',
        }}
        data-testid="buttonNewBlankProject"
      />

      <ActionButton
        title={CONTENT_IMPROVER_LABEL}
        Icon={IconImprove}
        tooltip={CONTENT_IMPROVER_TOOLTIP}
        onClick={() => onAction(WIDGET_ACTION.improveContent)}
        disabled={disabled}
        customColors={{
          backgroundDefault: 'var(--color-pastel-pink)',
          backgroundHover: '#D96AA6',
          icon: '#D96AA6',
        }}
        data-testid="buttonImproveContent"
      />

      <ActionButton
        title={TEMPLATES_LABEL}
        Icon={IconTemplates}
        tooltip={TEMPLATES_TOOLTIP}
        onClick={() => onAction(WIDGET_ACTION.openTemplateGallery)}
        disabled={disabled}
        customColors={{
          backgroundDefault: 'var(--color-primary-light)',
          backgroundHover: 'var(--color-primary-active)',
          icon: 'var(--color-primary-active)',
        }}
        data-testid="buttonTemplateGallery"
      />

      <ActionButton
        title={BLOG_WIZARD_LABEL}
        Icon={IconBlogWizard}
        tooltip={BLOG_WIZARD_TOOLTIP}
        onClick={() => onAction(WIDGET_ACTION.createBlogProject)}
        disabled={disabled}
        customColors={{
          backgroundDefault: 'var(--color-pastel-green)',
          backgroundHover: 'var(--color-success-active)',
          icon: 'var(--color-success-active)',
        }}
        data-testid="buttonNewBlogProject"
      />

      <ActionButton
        title={LOCALIZATION_LABEL}
        Icon={IconGlobe}
        tooltip={LOCALIZATION_TOOLTIP}
        onClick={() => onAction(WIDGET_ACTION.createLocalizationProject)}
        disabled={disabled}
        customColors={{
          backgroundDefault: 'var(--color-pastel-yellow)',
          backgroundHover: 'var(--color-warning-active)',
          icon: 'var(--color-warning-active)',
        }}
        data-testid="buttonNewLocalizationProject"
      />
    </div>
  );
};
